import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid'
import {
    addAudioFile,
    deleteAudioFile,
    getAudioFile,
    isDesktop,
    loadResourceLibrary,
    migrateAudioFilesToDesktop,
    saveResourceLibrary,
} from '../services/resourceStorage.js'

const STORAGE_KEY = 'hasp-audio-library'

/**
 * Project audio library — metadata only, modeled directly on fontStore.js:
 * Pinia + localStorage for the metadata, IndexedDB (src/utils/audioDB.js) for
 * the bytes — in the desktop app both live in its SQLite store instead (see
 * services/resourceStorage.js) — `uuid` as the permanent Designer identity. Like the image and
 * font libraries it lives *outside* the design file, so nothing is ever
 * base64-inlined into a saved project.
 *
 * Export identity is the `filename` (images work the same way: their `src` is
 * just `L:/<filename>` — see Exporter._stripImageUuids's comment). The device
 * path is therefore always `L:/<filename>`, which is exactly what the device
 * resource upload writes and what the exported `audio L:/<filename>` command
 * refers to. `uuid` is what widgets reference (see ObjectExport's audio action
 * handling), so renaming the display name — or remapping the file — never
 * breaks an action.
 *
 * Local WAV clips and external URL streams share this library and resource
 * identity. Streams persist only their uuid, name and original URL; their
 * `audio_url` type keeps them out of local-file packaging and storage.
 */

const PACKAGE_FOLDER = 'audio'

/** The command sentinel stored on a widget for the typed `audio.play` action. */
export const AUDIO_PLAY_COMMAND = 'audio.play'
export const AUDIO_URL_RESOURCE_TYPE = 'audio_url'
export const AUDIO_SYNTH_RESOURCE_TYPE = 'synth'

export function validateAudioStreamUrl(input) {
  let parsed
  try {
    parsed = new URL(input)
  } catch {
    throw new Error('Enter a valid audio URL.')
  }
  if (!['http:', 'https:'].includes(parsed.protocol) || !parsed.hostname) {
    throw new Error('Audio URLs must use HTTP or HTTPS.')
  }
  return input.trim()
}

export function defaultAudioStreamName(input) {
  try {
    const url = new URL(input)
    const segment = decodeURIComponent(url.pathname.split('/').pop() ?? '')
    const name = segment.replace(/\.[^.]*$/, '').trim()
    return name || url.hostname
  } catch {
    return ''
  }
}

/**
 * The on-device path for an audio resource, using the same `L:/<filename>`
 * convention as images (HaspImage's `src`) and fonts (a Font Resource's
 * `file`). Returns null when the resource has no filename yet — callers must
 * treat that as "unresolved" rather than emitting `L:/undefined`.
 */
export function audioDevicePath(resource) {
  return ![AUDIO_URL_RESOURCE_TYPE, AUDIO_SYNTH_RESOURCE_TYPE].includes(resource?.type) && resource?.filename
    ? `L:/${resource.filename}`
    : null
}

export function audioPlaybackCommand(resource, synthFilename = null) {
  if (resource?.type === AUDIO_URL_RESOURCE_TYPE) {
    try {
      return `audio ${validateAudioStreamUrl(resource.url)}`
    } catch {
      return null
    }
  }
  if (resource?.type === AUDIO_SYNTH_RESOURCE_TYPE) {
    return synthFilename ? `audio L:/${synthFilename}` : null
  }
  const path = audioDevicePath(resource)
  return path ? `audio ${path}` : null
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

export const useAudioStore = defineStore('audio', {
  // The browser library loads synchronously; the desktop app's SQLite
  // library arrives through load() at startup.
  state: () => ({
    clips: isDesktop() ? [] : loadFromStorage(),
    loaded: !isDesktop(),
  }),
  getters: {
    all: (state) => state.clips,
  },
  actions: {
    /** Load the desktop app's SQLite library (no-op in the browser, which loads synchronously). */
    async load() {
      if (!isDesktop()) return
      this.clips = await loadResourceLibrary('audio', STORAGE_KEY, { migrateEntries: migrateAudioFilesToDesktop })
      this.loaded = true
    },

    _persist() {
      // Never let a save before load() replace the stored library with a partial one.
      if (!this.loaded) return
      try {
        saveResourceLibrary('audio', STORAGE_KEY, this.clips)
      } catch (e) {
        console.warn('Failed to persist audio library:', e)
      }
    },

    /**
     * A device filename not already taken by another resource — "boot.wav",
     * then "boot-2.wav", "boot-3.wav", … Unlike images/fonts (where two
     * resources may legitimately share a name and simply overwrite each other
     * on the plate), an audio action resolves to this exact path at export
     * time, so a collision would silently play the wrong clip.
     */
    _uniqueFilename(filename, exceptUuid = null) {
      const taken = new Set(this.clips.filter(c => c.uuid !== exceptUuid).map(c => c.filename))
      if (!taken.has(filename)) return filename
      const dot = filename.lastIndexOf('.')
      const stem = dot > 0 ? filename.slice(0, dot) : filename
      const ext = dot > 0 ? filename.slice(dot) : ''
      let n = 2
      while (taken.has(`${stem}-${n}${ext}`)) n++
      return `${stem}-${n}${ext}`
    },

    // --- CRUD ---

    /**
     * Import a `.wav` File as a new audio resource. This is the WAV-specific
     * adapter/import path — the only place in the codebase allowed to assume
     * an audio resource is backed by a WAV file. Returns the new resource's
     * uuid, or throws if the file isn't a WAV.
     */
    async importWavFile(file) {
      if (!/\.wav$/i.test(file.name)) {
        throw new Error('Only .wav files are supported.')
      }
      const data = await readFileAsDataURL(file)
      const storageId = await addAudioFile(data, file.name, file.type || 'audio/wav')
      const clip = {
        uuid: uuidv4(),
        name: file.name.replace(/\.wav$/i, ''),
        filename: this._uniqueFilename(file.name),
        packageFolder: PACKAGE_FOLDER,
        type: 'wav',
        storageId,                   // AudioDB row id for the imported bytes
        originalFilename: file.name, // never rewritten by collision handling
        metadata: { fileSize: file.size },
      }
      this.clips.push(clip)
      this._persist()
      return clip.uuid
    },

    /** Add URL metadata only. The URL is used later by firmware or native preview. */
    addAudioUrlResource({ name, url, uuid, mimeType } = {}) {
      const originalUrl = validateAudioStreamUrl(url ?? '')
      const resource = {
        uuid: uuid && !this.getAudio(uuid) ? uuid : uuidv4(),
        name: name?.trim() || defaultAudioStreamName(originalUrl) || 'Web audio',
        type: AUDIO_URL_RESOURCE_TYPE,
        url: originalUrl,
      }
      if (mimeType) resource.mimeType = mimeType
      this.clips.push(resource)
      this._persist()
      return resource.uuid
    },

    /** Add a metadata-only reference to an existing Synth Library UUID. */
    addSynthAudioResource({ name, synthId, uuid } = {}) {
      if (typeof synthId !== 'string' || !synthId.trim()) {
        throw new Error('Choose a synth from the Synth Library.')
      }
      const existing = this.getAudioBySynthId(synthId)
      if (existing) return existing.uuid
      const resource = {
        uuid: uuid && !this.getAudio(uuid) ? uuid : uuidv4(),
        name: name?.trim() || 'Synth',
        type: AUDIO_SYNTH_RESOURCE_TYPE,
        synthId,
      }
      this.clips.push(resource)
      this._persist()
      return resource.uuid
    },

    /**
     * Create a resource from bytes that already exist (a packaged
     * `audio/<name>.audio.json` envelope — see audioResourceAdapter's
     * commitPackageFile). Same resource shape importWavFile() produces; it just
     * doesn't re-derive anything from a File object. `filename` is taken from
     * the envelope verbatim, so a design's actions keep resolving to the same
     * device path they were saved with — collision handling only kicks in if a
     * *different* clip already owns that name.
     *
     * `uuid` is the resource's original Designer identity, carried in the
     * package envelope. It is honoured whenever it's still free, so a cold
     * import needs no reference remapping at all; a collision with a different
     * clip mints a fresh uuid instead, and the caller (DesignPackageLoader, via
     * the adapter's originalUuid()/remapReference()) rewrites the structured
     * action operands that pointed at the old one.
     */
    async addImportedClip({ uuid, filename, dataUrl, name, type = 'wav', mime = 'audio/wav', originalFilename, metadata }) {
      const storageId = await addAudioFile(dataUrl, filename, mime)
      const clip = {
        uuid: uuid && !this.getAudio(uuid) ? uuid : uuidv4(),
        name: name || filename.replace(/\.[^./]+$/, ''),
        filename: this._uniqueFilename(filename),
        packageFolder: PACKAGE_FOLDER,
        type,
        storageId,
        originalFilename: originalFilename ?? filename,
        metadata: metadata ?? {},
      }
      this.clips.push(clip)
      this._persist()
      return clip.uuid
    },
    removeAudio(uuid) {
      const clip = this.getAudio(uuid)
      if (!clip) return
      this.clips.splice(this.clips.indexOf(clip), 1)
      this._persist()
      // Duplicates can share a storageId (see duplicateAudio) — only free the
      // stored file once nothing else references it.
      const stillReferenced = clip.storageId != null && this.clips.some(c => c.storageId === clip.storageId)
      if (clip.storageId != null && !stillReferenced) deleteAudioFile(clip.storageId)
    },
    duplicateAudio(uuid) {
      const src = this.getAudio(uuid)
      if (!src) return null
      if (src.type === AUDIO_URL_RESOURCE_TYPE) {
        return this.addAudioUrlResource({
          name: src.name + ' (copy)',
          url: src.url,
          mimeType: src.mimeType,
        })
      }
      if (src.type === AUDIO_SYNTH_RESOURCE_TYPE) return null
      const copy = {
        ...JSON.parse(JSON.stringify(src)),
        uuid: uuidv4(),
        name: src.name + ' (copy)',
        filename: this._uniqueFilename(src.filename),
        // storageId is copied as-is — both resources point at the same stored
        // bytes, exactly like duplicateFont(). Cheap, and correct: removing
        // either one only frees the file when nothing else references it.
      }
      this.clips.push(copy)
      this._persist()
      return copy.uuid
    },
    getAudio(uuid) {
      return this.clips.find(c => c.uuid === uuid) ?? null
    },
    /** Lookup by device filename — the package dedup key (see the adapter). */
    getAudioByFilename(filename) {
      return this.clips.find(c => c.filename === filename) ?? null
    },
    getAudioByUrl(url) {
      return this.clips.find(c => c.type === AUDIO_URL_RESOURCE_TYPE && c.url === url) ?? null
    },
    getAudioBySynthId(synthId) {
      return this.clips.find(c => c.type === AUDIO_SYNTH_RESOURCE_TYPE && c.synthId === synthId) ?? null
    },
    updateAudio(uuid, patch) {
      const clip = this.clips.find(c => c.uuid === uuid)
      if (!clip) return
      if (clip.type === AUDIO_URL_RESOURCE_TYPE && patch.url !== undefined) {
        patch = { ...patch, url: validateAudioStreamUrl(patch.url) }
      }
      // A filename is a device path — keep it unique whichever way it's set.
      if (clip.type !== AUDIO_URL_RESOURCE_TYPE && patch.filename && patch.filename !== clip.filename) {
        patch = { ...patch, filename: this._uniqueFilename(patch.filename, uuid) }
      }
      Object.assign(clip, patch)
      this._persist()
    },

    /**
     * The imported bytes as a base64 data URL, or null when this resource has
     * no file. Directly usable both as `new Audio(src)` for the manager's
     * preview button and as the file-transfer payload — the same data-URL shape
     * fonts and images already upload with, so no playback or blob-URL
     * machinery is needed.
     */
    async getAudioDataURL(uuid) {
      const clip = this.getAudio(uuid)
      if (!clip || clip.type === AUDIO_URL_RESOURCE_TYPE || clip.storageId == null) return null
      const record = await getAudioFile(clip.storageId)
      return record?.data ?? null
    },
  },
})

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}
